import React, { useCallback, useEffect, useState } from 'react';
import { API_CONFIG } from '../config';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';
import Button from './ui/Button';
import ConfirmationModal from './ui/ConfirmationModal';

interface TrashedBiddingSheet {
  id: string;
  name: string;
  projectId?: string;
  projectName?: string;
  currency?: string;
  status?: string;
  deletedAt?: string;
  shotCount?: number;
  createdByName?: string;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('spellbook_token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function formatDate(value?: string): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

const BiddingTrashBinPanel: React.FC = () => {
  const { t } = useTranslation();
  const [sheets, setSheets] = useState<TrashedBiddingSheet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [purgeId, setPurgeId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_CONFIG.API_URL}/bidding/trash`, { headers: authHeaders() });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setSheets(Array.isArray(data.biddingSheets) ? data.biddingSheets : []);
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : t('system.backup.biddingTrashLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleRestore = async (id: string) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_CONFIG.API_URL}/bidding/${id}/restore`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || t('system.backup.biddingTrashRestoreFailed'));
      }
      setSuccess(t('system.backup.biddingTrashRestoreSuccess'));
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('system.backup.biddingTrashRestoreFailed'));
      setLoading(false);
    }
  };

  const handlePurge = async (id: string) => {
    setLoading(true);
    setError('');
    setSuccess('');
    setPurgeId(null);
    try {
      const res = await fetch(`${API_CONFIG.API_URL}/bidding/${id}/purge`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || t('system.backup.biddingTrashPurgeFailed'));
      }
      setSuccess(t('system.backup.biddingTrashPurgeSuccess'));
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('system.backup.biddingTrashPurgeFailed'));
      setLoading(false);
    }
  };

  const handleRestoreSelected = async () => {
    if (selected.size === 0) return;
    setLoading(true);
    setError('');
    setSuccess('');
    let ok = 0;
    let failed = 0;
    for (const id of selected) {
      try {
        const res = await fetch(`${API_CONFIG.API_URL}/bidding/${id}/restore`, {
          method: 'POST',
          headers: authHeaders(),
        });
        if (res.ok) ok += 1;
        else failed += 1;
      } catch {
        failed += 1;
      }
    }
    setSuccess(
      t('system.backup.biddingTrashRestoreBatch')
        .replace('{ok}', String(ok))
        .replace('{failed}', String(failed))
    );
    await refresh();
  };

  const toggleAll = (checked: boolean) => {
    if (!checked) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(sheets.map((s) => s.id)));
  };

  return (
    <div className="bg-gray-700 rounded-lg p-6 space-y-4 mt-6">
      <h3 className="text-lg font-semibold text-white mb-1 flex items-center">
        <ICONS.Trash className="w-5 h-5 mr-2" />
        {t('system.backup.biddingTrashTitle')}
      </h3>
      <p className="text-sm text-gray-300">{t('system.backup.biddingTrashHint')}</p>
      <p className="text-xs text-amber-300">{t('system.backup.biddingTrashSuperAdminOnly')}</p>

      {error && (
        <div className="text-sm text-red-300 bg-red-900/30 border border-red-700 rounded p-2">{error}</div>
      )}
      {success && (
        <div className="text-sm text-green-300 bg-green-900/30 border border-green-700 rounded p-2">{success}</div>
      )}

      <div className="flex flex-nowrap items-center gap-2 overflow-x-auto">
        <Button variant="secondary" size="sm" onClick={refresh} disabled={loading} className="whitespace-nowrap shrink-0">
          <ICONS.RefreshCw className="w-4 h-4 mr-1" />
          {t('system.backup.trashRefresh')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleRestoreSelected}
          disabled={loading || selected.size === 0}
          className="whitespace-nowrap shrink-0"
        >
          {t('system.backup.biddingTrashRestoreSelected')}
        </Button>
        <span className="text-sm text-gray-400 whitespace-nowrap shrink-0">
          {t('system.backup.biddingTrashCount').replace('{count}', String(sheets.length))}
        </span>
      </div>

      <div className="max-h-64 overflow-auto border border-gray-600 rounded">
        <table className="w-full text-xs text-left">
          <thead className="bg-gray-800 sticky top-0 text-gray-300">
            <tr>
              <th className="p-2 w-8">
                <input
                  type="checkbox"
                  checked={sheets.length > 0 && sheets.every((s) => selected.has(s.id))}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </th>
              <th className="p-2">{t('system.backup.biddingTrashColName')}</th>
              <th className="p-2">{t('system.backup.biddingTrashColProject')}</th>
              <th className="p-2">{t('system.backup.biddingTrashColShots')}</th>
              <th className="p-2">{t('system.backup.biddingTrashColDeleted')}</th>
              <th className="p-2 w-40">{t('system.backup.biddingTrashColActions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading && sheets.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-4 text-center text-gray-400">
                  {t('system.backup.trashLoading')}
                </td>
              </tr>
            ) : sheets.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-4 text-center text-gray-400">
                  {t('system.backup.biddingTrashEmpty')}
                </td>
              </tr>
            ) : (
              sheets.map((sheet) => (
                <tr key={sheet.id} className="border-t border-gray-600 hover:bg-gray-800/50">
                  <td className="p-2">
                    <input
                      type="checkbox"
                      checked={selected.has(sheet.id)}
                      onChange={(e) => {
                        const next = new Set(selected);
                        if (e.target.checked) next.add(sheet.id);
                        else next.delete(sheet.id);
                        setSelected(next);
                      }}
                    />
                  </td>
                  <td className="p-2 text-white font-medium">{sheet.name}</td>
                  <td className="p-2 text-gray-300">{sheet.projectName || '—'}</td>
                  <td className="p-2 text-gray-300">{Number(sheet.shotCount) || 0}</td>
                  <td className="p-2 text-gray-400 whitespace-nowrap">{formatDate(sheet.deletedAt)}</td>
                  <td className="p-2">
                    <div className="flex flex-nowrap gap-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="whitespace-nowrap shrink-0 !px-2 !py-1"
                        disabled={loading}
                        onClick={() => handleRestore(sheet.id)}
                      >
                        {t('system.backup.biddingTrashRestore')}
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        className="whitespace-nowrap shrink-0 !px-2 !py-1"
                        disabled={loading}
                        onClick={() => setPurgeId(sheet.id)}
                      >
                        {t('system.backup.biddingTrashPurge')}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ConfirmationModal
        isOpen={!!purgeId}
        onClose={() => setPurgeId(null)}
        onConfirm={() => {
          if (purgeId) void handlePurge(purgeId);
        }}
        type="danger"
        title={t('system.backup.biddingTrashPurgeConfirmTitle')}
        message={t('system.backup.biddingTrashPurgeConfirmMessage')}
        confirmText={t('system.backup.biddingTrashPurge')}
      />
    </div>
  );
};

export default BiddingTrashBinPanel;
